import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { Button } from "~/features/ui/button";
import { Input } from "~/features/ui/form/input";
import { Checkbox } from "~/features/ui/form/checkbox";
import { PhoneNumber } from "~/features/ui/form/phone-number";
import { useFormState } from "~/features/ui/form/use-form-state";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { getRecaptchaToken } from "~/features/re-captcha/utils";
import { prefixes } from "~/features/blocks/inventory/marketo-form/options";
import Loading from "~/public/icons/loading.svg";
import ArrowRight from "~/public/icons/arrow-right.svg";
import { Icon } from "~/features/ui/icon";
export type ReserveResult = "success" | "claimed" | "error";
type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
  consent: boolean;
};
type Props = {
  /** Stable product slug, resolved against the published catalog by the API. */
  productSlug?: string | null;
  /** Selected size — stored as the order's `size`. */
  size?: string | null;
  onResult: (result: ReserveResult, email: string) => void;
};
const REQUIRED = {
  required: true
} as const;
export function ReserveGearForm({
  productSlug,
  size,
  onResult
}: Props) {
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  const [status, setStatus] = useFormState({
    state: "IDLE"
  });
  const {
    register,
    handleSubmit,
    control,
    formState: {
      errors
    }
  } = useForm<FormValues>();
  const isLoading = status.state === "LOADING";
  const onSubmit = handleSubmit(async values => {
    setStatus({
      state: "LOADING"
    });
    try {
      const recaptchaToken = await getRecaptchaToken();
      // Phone is "+<prefix> <number>"; a prefix without a number is empty.
      const phoneRaw = (values.phone ?? "").trim();
      const phoneNumber = phoneRaw.split(" ")[1] ? phoneRaw : "";
      const res = await fetch("/api/reserve-gear", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          state: values.state,
          postalCode: values.postalCode,
          country: values.country,
          phone: phoneNumber,
          productSlug: productSlug ?? undefined,
          consent: values.consent,
          recaptchaToken,
          size: size ?? undefined
        })
      });
      if (res.status === 200) {
        onResult("success", values.email);
      } else if (res.status === 409) {
        onResult("claimed", values.email);
      } else {
        onResult("error", values.email);
      }
    } catch {
      onResult("error", values.email);
    } finally {
      setStatus({
        state: "IDLE"
      });
    }
  });
  return <_StyledForm onSubmit={onSubmit} $_css={[{
    "display": "flex",
    "minHeight": "0",
    "flex": "1 1 0%",
    "flexDirection": "column"
  }]}>
      <_StyledDiv $_css2={[{
      "display": "flex",
      "flex": "1 1 0%",
      "flexDirection": "column",
      "gap": "2.4rem",
      "overflowY": "auto",
      "paddingLeft": "2.4rem",
      "paddingRight": "2.4rem",
      "paddingTop": "3.2rem",
      "paddingBottom": "3.2rem",
      "@media (min-width: 1024px)": {
        "paddingLeft": "4rem",
        "paddingRight": "4rem"
      }
    }]}>
        <_StyledP $_css3={[{
        "fontSize": "1.6rem",
        "lineHeight": "1.5",
        "fontWeight": "400",
        "--tw-text-opacity": "1",
        "color": "rgb(92 92 92 / var(--tw-text-opacity))",
        "@media (min-width: 1024px)": {
          "fontSize": "1.8rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.018rem",
          "fontWeight": "400"
        }
      }]}>
          Enter your shipping details and we&rsquo;ll get your gear on its way.
        </_StyledP>

        <Input label="First Name" required register={register("firstName", REQUIRED)} error={errors.firstName && "Required"} />
        <Input label="Last Name" required register={register("lastName", REQUIRED)} error={errors.lastName && "Required"} />
        <Input label="Email" type="email" required register={register("email", REQUIRED)} error={errors.email && "Required"} />
        <Input label="Address Line 1" required register={register("address1", REQUIRED)} error={errors.address1 && "Required"} />
        <Input label="Address Line 2" register={register("address2")} />

        <_StyledDiv2 $_css4={[{
        "display": "grid",
        "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
        "gap": "1.6rem",
        "@media (min-width: 1024px)": {
          "gridTemplateColumns": "repeat(2, minmax(0, 1fr))"
        }
      }]}>
          <Input label="City" required register={register("city", REQUIRED)} error={errors.city && "Required"} />
          <Input label="State / Province" required register={register("state", REQUIRED)} error={errors.state && "Required"} />
          <Input label="Postal Code" required register={register("postalCode", REQUIRED)} error={errors.postalCode && "Required"} />
          <Input label="Country" required register={register("country", REQUIRED)} error={errors.country && "Required"} />
        </_StyledDiv2>

        <Controller control={control} name="phone" render={({
        field
      }) => <PhoneNumber label="Phone number" prefixes={prefixes} value={field.value ?? ""} onChange={field.onChange} />} />

        <Checkbox label="I agree to allow Ouster to store and process my personal data." register={register("consent", REQUIRED)} error={errors.consent && "Please accept to continue"} />
      </_StyledDiv>

      <_StyledDiv3 $_css5={[{
      "position": "sticky",
      "bottom": "0",
      "zIndex": "10",
      "borderTopWidth": "1px",
      "padding": "1.6rem"
    }, themeStyle.border, themeStyle.background]}>
        <Button type="submit" variant="primary" fullWidth icon={isLoading ? undefined : ArrowRight} disabled={isLoading}>
          {isLoading ? <Icon icon={Loading} /> : "Reserve Gear"}
        </Button>
      </_StyledDiv3>
    </_StyledForm>;
}
var _StyledForm = /*#__PURE__*/_styled("form").withConfig({
  componentId: "sc-sf-mqad6r-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-mqad6r-1"
})`${p => p.$_css2}`;
var _StyledP = _styled3("p").withConfig({
  componentId: "sc-sf-mqad6r-2"
})`${p => p.$_css3}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-mqad6r-3"
})`${p => p.$_css4}`;
var _StyledDiv3 = /*#__PURE__*/_styled5("div").withConfig({
  displayName: "reserve-gear-form___StyledDiv3",
  componentId: "sc-1fyl0q5-0"
})(["", ""], p => p.$_css5);